// Who is reading the Guide, and which modules they should see.
//
// Three levels, mirroring how the app itself gates its sidebar
// (Layout.jsx canAccessNavItem):
//   super_admin  an OpCo admin (OpCos switched on) - also holds the org Admin role
//   admin        org Admin (and a Reader, who sees the admin pages view-only)
//   user         everyone else: Template Manager, Approver, Reviewer, User
//
// Kept free of React so the rules can be tested on their own.

export const AUDIENCES = ['super_admin', 'admin', 'user']

export const AUDIENCE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  user: 'User',
}

// Stored role -> label shown in the app. Approver and Reviewer are swapped
// relative to the stored values (reviewer = "Approver", sender = "Reviewer").
export const ROLE_LABELS = {
  admin: 'Admin',
  template_manager: 'Template Manager',
  reviewer: 'Approver',
  sender: 'Reviewer',
  user: 'User',
  reader: 'Reader',
}

export function getGuideAudience({ user, opcoEnabled = false, isOpcoAdmin = false, readerEnabled = false } = {}) {
  if (opcoEnabled && isOpcoAdmin) return 'super_admin'
  if (user?.role === 'admin') return 'admin'
  if (readerEnabled && user?.role === 'reader') return 'admin'
  return 'user'
}

// Audiences a person may preview: their own level and the ones below it.
export function previewableAudiences(audience) {
  const i = AUDIENCES.indexOf(audience)
  return i < 0 ? ['user'] : AUDIENCES.slice(i)
}

function featureOn(module, { features = {}, opcoEnabled = false }) {
  const f = module.feature
  if (!f) return true
  // A list means "any of these" (e.g. Business Central or NetSuite).
  if (Array.isArray(f)) return f.some((one) => featureOn({ feature: one }, { features, opcoEnabled }))
  if (f === 'opco') return opcoEnabled
  // Connectors fail open (on unless explicitly off); BC / NetSuite fail closed.
  if (f === 'connectors') return features.connectors !== false
  return features[f] === true
}

// `role` is the person's stored role. Pass undefined when previewing a lower
// level than their own, so role-limited modules (Approvals, Templates) stay visible.
export function visibleModules(modules, { audience, role, features, opcoEnabled }) {
  return modules.filter((m) => {
    if (!m.audiences.includes(audience)) return false
    if (!featureOn(m, { features, opcoEnabled })) return false
    if (audience === 'user' && role) {
      if (m.userRoles && !m.userRoles.includes(role)) return false
      if (m.hiddenForRoles && m.hiddenForRoles.includes(role)) return false
    }
    return true
  })
}
